<template>
  <div>
    <RevForm
      class="flex h-full flex-col justify-between"
      :initial-values="INITIAL_FORM_VALUES"
      :validate="validate()"
      @submit="handleSubmit"
    >
      <template #default="{ values, errors }">
        <div class="space-y-12">
          <DynamicAddressFieldValidatorsErrorLogger
            :errors
            :form-values="values"
            logger-scope="SELLER_OPTION_ADDRESS_FORM"
          />
          <AddressFields
            v-model:city="values.city"
            v-model:company="values.company"
            v-model:country="values.country"
            v-model:firstName="values.firstName"
            v-model:firstNamePronunciation="values.firstNamePronunciation"
            v-model:lastName="values.lastName"
            v-model:lastNamePronunciation="values.lastNamePronunciation"
            v-model:phone="values.phone"
            v-model:postal-code="values.postalCode"
            v-model:state-or-province="values.stateOrProvince"
            v-model:street="values.street"
            :errors
            :feature-code="FeatureCode.WEB_MERCHANT_OPTION_ADDRESS"
            :optional-fields="[]"
          />

          <RevInputText
            id="label"
            v-model="values.label"
            :description="i18n(translations.labelDescription)"
            :error="errors.label"
            :label="i18n(translations.label)"
          />
          <RevButton full-width="always" type="submit" variant="primary">
            {{ address ? i18n(translations.save) : i18n(translations.create) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { merchantsAPI } from '@backmarket/http-api'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import translations from './AddressForm.translations'
import { type FormValues, METADATA } from './AdressForm.constants'

const { getDynamicAddressFieldValidators } = useDynamicAddressFieldValidators()

const props = defineProps<{
  address: merchantsAPI.Address | null
}>()

const emit = defineEmits(['address-submitted'])

const { openErrorToast } = useTheToast()
const i18n = useI18n()
const { isFeatureEnabled } = useBusinessFeaturesStore()

const isKanaNameEnabled = !!isFeatureEnabled('kanaNameEnabled')

const INITIAL_FORM_VALUES: FormValues = {
  firstName: props.address?.firstName || '',
  lastName: props.address?.lastName || '',
  company: props.address?.company || '',
  city: props.address?.city || '',
  postalCode: props.address?.postalCode || '',
  country: props.address?.country || '',
  // TODO: Could be better to use stateOrProvince instead of state
  stateOrProvince: props.address?.state || '',
  // TODO: Why are we using addressLine instead of street ?
  street: props.address?.addressLine || '',
  // TODO: Could be better to use phone instead of phoneNumber
  phone: props.address?.phoneNumber || '',
  label: props.address?.label || '',
  ...insertIf(isKanaNameEnabled, {
    firstNamePronunciation: props.address?.firstNamePronunciation || '',
    lastNamePronunciation: props.address?.lastNamePronunciation || '',
  }),
}

const addressValidation = await getDynamicAddressFieldValidators({
  featureFlag: 'FF_ENABLE_ADDRESS_FIELD_VALIDATION_SELLER_OPTION_ADDRESS_FORM',
  initialFormValues: INITIAL_FORM_VALUES,
  fallbackValidators: {
    ...AddressFieldsValidators(i18n),
    label: {
      required: required(i18n(translations.required)),
    },
  },
  metadata: METADATA,
})

function validate() {
  return makeValidate<FormValues>(addressValidation)
}

async function handleSubmit(body: FormValues) {
  // Merchant API do not support the Address RFC format
  const payload = {
    ...omit(body, ['stateOrProvince', 'street', 'phone']),
    state: body.stateOrProvince,
    addressLine: body.street,
    phoneNumber: body.phone,
  }
  try {
    if (props.address) {
      await $httpFetch(merchantsAPI.patchAddress, {
        pathParams: { id: props.address.id },
        body: payload,
      })
    } else {
      await $httpFetch(merchantsAPI.postAddress, { body: payload })
    }

    emit('address-submitted')
  } catch {
    openErrorToast()
  }
}
</script>
